import { DocsCallout, DocsContent, DocsLayout, H2, H3 } from '@/components/docs'
import { Card } from '@/components/ui/card'

export default function DocsAuditSchema() {
  return (
    <DocsLayout>
      <DocsContent
        title="Designing an Audit Investigation Schema"
        description="How to model a forensic / financial-audit knowledge graph in XENDREL. Covers the critical design decisions, a full Big-4-style schema, and a simplified Codeseed example."
      >
        <H2 id="why-a-dedicated-schema">Why a dedicated schema</H2>
        <p className="text-foreground mb-4 leading-relaxed">
          The default <code>POLE</code> schema is tuned for law-enforcement investigations: cameras,
          badges, crimes, phone calls. For a Big-4 style audit engagement (KPMG, Deloitte, EY, PwC)
          the centre of gravity is different — money movement, contracts, financial statements,
          governance roles. Reusing POLE for audit pollutes extractor prompts and forces auditors to
          translate every question into the wrong vocabulary.
        </p>
        <p className="text-foreground mb-6 leading-relaxed">
          This article explains how to think about an audit schema, documents the full{' '}
          <code>AUDIT_SCHEMA</code> seeded into XENDREL, and shows the simpler{' '}
          <code>AUDIT_SCHEMA_CODESEED</code> variant used for teaching and demos.
        </p>

        <H2 id="use-cases">Auditor use cases the schema must answer</H2>
        <p className="text-foreground mb-4 leading-relaxed">
          Before modelling entities, list the questions. The schema is a function of the questions —
          not the other way around.
        </p>

        <H3 id="basic-investigation">Basic investigation (SP-204)</H3>
        <ul className="space-y-2 my-4 text-foreground list-disc pl-6">
          <li>Show me the 10 biggest transactions.</li>
          <li>Show me transactions for this person / account / company.</li>
          <li>Show me connections on this company.</li>
          <li>This account is flagged as fraud — show all connected transactions.</li>
          <li>Show me the risk profile of this company (bad cashflow, customer concentration).</li>
          <li>
            Does a contract exist that justifies this transaction? (employment contract, service
            contract).
          </li>
          <li>Recurring transactions, F&amp;L patterns.</li>
          <li>Circles (A → B → C → A), anomaly detection.</li>
        </ul>

        <H3 id="deeper-audit">What Big-4 auditors actually ask for</H3>
        <ul className="space-y-2 my-4 text-foreground list-disc pl-6">
          <li>
            <strong>Journal-entry testing:</strong> unusual posting users, weekend/period-end
            entries, round numbers, manual vs. system postings, near-mirror reversals.
          </li>
          <li>
            <strong>Cut-off testing:</strong> revenue/expenses booked in the wrong period (invoice
            date vs. booking date gap).
          </li>
          <li>
            <strong>Three-way match:</strong> PO ↔ goods-receipt ↔ invoice mismatches.
          </li>
          <li>
            <strong>Duplicate invoices</strong> and <strong>amount splitting</strong> just under
            approval thresholds.
          </li>
          <li>
            <strong>Benford's Law</strong> deviations on leading digits.
          </li>
          <li>
            <strong>Related-party detection:</strong> shared IBAN / address / phone / UBO between
            supposedly independent counterparties.
          </li>
          <li>
            <strong>Sanctions / PEP / AML:</strong> OFAC, EU, UN, national PEP lists; high-risk
            jurisdictions (FATF grey/black).
          </li>
          <li>
            <strong>Revenue recognition</strong> (IFRS 15): contract → performance obligation →
            invoice → payment chain completeness.
          </li>
          <li>
            <strong>Payroll anomalies:</strong> ghost employees, contractor-vs-employee
            misclassification (e.g. Deel payroll).
          </li>
          <li>
            <strong>VAT chain integrity</strong> (carousel/missing-trader), transfer-pricing
            exposure.
          </li>
          <li>
            <strong>Segregation of duties:</strong> same user created vendor + approved invoice +
            posted payment.
          </li>
          <li>
            <strong>Document integrity:</strong> PDF metadata anomalies, signatures vs. authorised
            signatory list, contract clause extraction.
          </li>
          <li>
            <strong>Going concern:</strong> cash runway, covenant breaches, supplier/customer
            concentration, aged AR/AP.
          </li>
        </ul>

        <H2 id="critical-design">Most critical schema-design decisions</H2>
        <p className="text-foreground mb-4 leading-relaxed">
          These are the calls that are <em>hard to reverse</em> later. Worth pinning down first.
        </p>

        <DecisionCard
          n="1"
          title="Transaction as NODE, not edge"
          body="The single biggest decision. Modelling a transaction as a relationship between two accounts blocks half the audit use cases: you cannot attach invoices, contracts, findings, user actions, or similarity edges to an edge. Every mature fraud/audit graph models transactions as nodes."
        />
        <DecisionCard
          n="2"
          title="Bi-temporal everything"
          body="Every fact needs valid_from / valid_to (when true in the world) AND recorded_at / source_doc_id (when we learned it). Auditors constantly ask 'what did we know as of date X' and 'was this true at date Y'. Cheap to add now, painful to retrofit."
        />
        <DecisionCard
          n="3"
          title="Raw vs. resolved separation"
          body="Bank narratives and invoice parties are messy. Model Counterparty as a distinct raw node, linked to canonical Person/Company by RESOLVES_TO with confidence + resolver metadata. Never silently merge — auditors need the evidence chain."
        />
        <DecisionCard
          n="4"
          title="Evidence as first-class edges"
          body="Every extracted fact points at Chunk / Document via EVIDENCED_BY {page, bbox, quote, confidence}. KPMG will ask 'show me the source' on every single claim. A confidence float on the entity is not enough."
        />
        <DecisionCard
          n="5"
          title="Derived vs. asserted nodes"
          body="Split facts-from-documents (OCR/LLM extracted) from analysis-produced findings (RiskFinding, Anomaly, Cluster, Cycle). Different lifecycles, different trust levels, different re-computation triggers. Tag with origin: extracted | derived | manual."
        />
        <DecisionCard
          n="6"
          title="Money is a value object"
          body="Always carry {amount, currency, fx_rate_to_base, fx_date, base_amount}. Multi-currency audits break without this. A bare float on a Transaction will cause reporting errors within weeks."
        />
        <DecisionCard
          n="7"
          title="Periods as nodes"
          body="Fiscal Period nodes (month/quarter/year with the company's fiscal-year offset) anchor statements, aggregations and comparatives. Don't rely on ad-hoc date-range queries scattered through the codebase."
        />
        <DecisionCard
          n="8"
          title="Chart of accounts as a taxonomy"
          body="LedgerLine → ON_ACCOUNT → AccountCode with taxonomy + category lets you query by meaning ('all personnel costs') not by magic strings ('521')."
        />
        <DecisionCard
          n="9"
          title="Time-sliced roles"
          body="All governance / employment edges carry {from, to}. 'Who was director when this transaction happened?' is a core question. A single HOLDS_ROLE edge with a role property replaces 5+ separate labels (director, partner, shareholder, UBO)."
        />
        <DecisionCard
          n="10"
          title="Case isolation vs. cross-case intelligence"
          body="Sanctions lists, PEPs, chart-of-accounts taxonomies, Law references are shared across cases. Transactions, counterparties, findings are case-scoped. Decide once which labels cross the case_id boundary — or you'll carry 50 copies of the OFAC list."
        />
        <DecisionCard
          n="11"
          title="Document kind is a controlled vocabulary"
          body="bank_statement, income_statement, liabilities_ledger, invoice, employment_contract, service_contract, share_transfer, founding_deed, power_of_attorney, NDA, tax_return… Drives extractors, validators, UI chips."
        />
        <DecisionCard
          n="12"
          title="Identifier hierarchy for SAME_AS"
          body="Decide match precedence: IBAN > VAT ID > (org_no + country) > (name + address) fuzzy. Store the key that triggered each merge so auditors can challenge it."
        />
        <DecisionCard
          n="13"
          title="Actions and actors"
          body="UserAccount, SystemEvent, POSTED_JOURNAL, APPROVED_JOURNAL. Without this, segregation-of-duties and user-behavior analytics are impossible. Often skipped — then audit-trail questions can't be answered."
        />
        <DecisionCard
          n="14"
          title="Obligations — the 'expected' graph"
          body="A contract implies expected invoices/payments on a schedule. Modelling Obligation nodes ('expected €X monthly from Y under contract Z') lets you detect MISSING transactions, not just suspicious present ones. This is completeness testing."
        />
        <DecisionCard
          n="15"
          title="Negative space matters"
          body="Auditors care about what SHOULD be there and isn't — missing contract for a recurring payment, missing VAT registration for a supplier, missing UBO filing. Design queries around absence, which requires modelling the expected side."
        />

        <DocsCallout variant="tip" title="Rule of thumb">
          If a question starts with "show me a transaction that has no…" and your schema can't
          answer it, you have a completeness gap. Fix it with Obligation nodes and explicit
          expected-vs-actual edges.
        </DocsCallout>

        <H2 id="full-schema">AUDIT_SCHEMA — the full schema</H2>
        <p className="text-foreground mb-4 leading-relaxed">
          Seeded at <code>src/apps/api/alembic/seed_data/audit_schema.json</code>. Designed for real
          Big-4 engagements. Summary of node groups:
        </p>

        <GroupTable
          rows={[
            ['People & governance', 'Person, Company, Role, UserAccount, TaxId, Jurisdiction'],
            ['Money movement', 'BankAccount, Transaction, Counterparty'],
            ['Accounting', 'JournalEntry, LedgerLine, AccountCode'],
            ['Commercial', 'Invoice, PurchaseOrder, GoodsReceipt, Contract, Obligation'],
            ['Reporting', 'FinancialStatement, Period, LiabilityItem, TaxFiling'],
            ['Risk & compliance', 'SanctionListEntry, RiskFinding, Anomaly, Cycle, Cluster'],
            ['Provenance', 'Case, Document, Chunk, Address, Phone, Email, Law'],
          ]}
        />

        <p className="text-foreground mb-4 leading-relaxed">Key relationship shapes:</p>
        <pre className="bg-card/50 border border-border rounded-md p-4 overflow-x-auto text-xs md:text-sm my-4">
          {`(Person)-[:HOLDS_ROLE {from, to}]->(Role)-[:ROLE_AT]->(Company)
(Person|Company)-[:UBO_OF {share_pct, as_of}]->(Company)
(Person|Company)-[:OWNS_ACCOUNT {from, to}]->(BankAccount)

(BankAccount)-[:TRANSACTION_OUT]->(Transaction)-[:TRANSACTION_IN]->(BankAccount)
(Transaction)-[:COUNTERPARTY]->(Counterparty)-[:RESOLVES_TO {confidence}]->(Person|Company)
(Transaction)-[:SETTLES {amount}]->(Invoice)-[:UNDER_CONTRACT]->(Contract)
(Transaction)-[:FULFILS]->(Obligation)<-[:IMPLIES_OBLIGATION]-(Contract)

(JournalEntry)-[:HAS_ENTRY]->(LedgerLine)-[:ON_ACCOUNT]->(AccountCode)
(UserAccount)-[:POSTED_JOURNAL {at}]->(JournalEntry)
(UserAccount)-[:APPROVED_JOURNAL {at}]->(JournalEntry)
(JournalEntry)-[:REVERSES]->(JournalEntry)

(FinancialStatement)-[:HAS_LINE]->(LedgerLine)
(FinancialStatement)-[:FOR_COMPANY]->(Company)
(FinancialStatement)-[:FOR_PERIOD]->(Period)

(Person|Company)-[:MATCHES_LIST {score, decision}]->(SanctionListEntry)
(RiskFinding)-[:INVOLVES]->(Transaction|Account|Company)
(Transaction)-[:SIMILAR_TO {score, reason}]->(Transaction)

(*)-[:EVIDENCED_BY {page, bbox, quote, confidence}]->(Chunk)-[:PART_OF]->(Document)`}
        </pre>

        <H2 id="codeseed-schema">AUDIT_SCHEMA_CODESEED — the simplified example</H2>
        <p className="text-foreground mb-4 leading-relaxed">
          Seeded at <code>src/apps/api/alembic/seed_data/audit_schema_codeseed.json</code>. Not a
          real audit — a teaching schema covering the Codeseed s.r.o. document set (bank statements,
          income statements, liabilities ledger, Deel employment contract, share transfer deed,
          invoices, NDA).
        </p>

        <p className="text-foreground mb-4 leading-relaxed">
          Stripped compared to the full schema:
        </p>
        <ul className="space-y-2 my-4 text-foreground list-disc pl-6">
          <li>No JournalEntry / AccountCode taxonomy (no ERP source in the demo set).</li>
          <li>No PurchaseOrder / GoodsReceipt / Obligation (no three-way-match scope).</li>
          <li>No SanctionListEntry / Jurisdiction / TaxFiling.</li>
          <li>No UserAccount / segregation-of-duties graph.</li>
          <li>
            Single <code>RiskFinding</code> type; no separate Anomaly/Cycle/Cluster nodes.
          </li>
          <li>Money kept as amount + currency; no FX normalisation.</li>
        </ul>

        <p className="text-foreground mb-4 leading-relaxed">What it still answers:</p>
        <GroupTable
          rows={[
            ['Top-N transactions', 'Transaction.amount + Period filter'],
            [
              'Transactions for company X',
              'Company → OWNS_ACCOUNT → BankAccount → TRANSACTION_OUT/IN → Transaction',
            ],
            [
              'Connections on a company',
              'Role / OWNS_ACCOUNT / invoice counterparties / shared addresses',
            ],
            ['Contract validation', 'Transaction → SETTLES → Invoice → UNDER_CONTRACT → Contract'],
            ['Recurring payments', 'Transaction.recurrence_key groupings + SIMILAR_TO edges'],
            ['Concentration risk', 'FinancialStatement.LedgerLine aggregates by counterparty'],
            ['Circles', 'Directed cycle detection over TRANSACTION_OUT/IN'],
          ]}
        />

        <H2 id="document-mapping">Mapping Codeseed documents to the schema</H2>
        <GroupTable
          rows={[
            ['Bank statements (SK04 09…_N)', 'BankAccount, Transaction, Counterparty'],
            [
              'Výsledovka komplexná (monthly)',
              'FinancialStatement {kind: income_statement} + LedgerLine + Period',
            ],
            [
              'Kniha záväzkov neuhradené',
              'FinancialStatement {kind: liabilities_ledger} + LedgerLine per supplier row',
            ],
            [
              'Deel contracts (Codeseed, Matus Smolen)',
              'Contract {kind: service|employment} + PARTY edges',
            ],
            ['Deel invoices (zip)', 'Invoice + ISSUED_BY + BILLED_TO + UNDER_CONTRACT + SETTLES'],
            ['VIA AI invoices', 'Invoice + ISSUED_BY (VIA AI) + BILLED_TO (Codeseed)'],
            ['Zakladateľská listina', 'Contract {kind: founding_deed} + PARTY + HOLDS_ROLE'],
            [
              'Zmluva o prevode obchodného podielu',
              'Contract {kind: share_transfer} + time-sliced HOLDS_ROLE updates',
            ],
            [
              'Vyhlásenie / Rozhodnutie jediného spoločníka',
              'Contract {kind: sole_shareholder_resolution}',
            ],
            ['PLNOMOCENSTVO ORSR', 'Contract {kind: power_of_attorney}'],
            ['SAIVA AI NDA', 'Contract {kind: nda} + PARTY'],
          ]}
        />

        <DocsCallout variant="info" title="Where to go next">
          See the seeded JSON files under <code>src/apps/api/alembic/seed_data/</code>. Activate a
          schema by toggling <code>is_active</code> and re-running the seed — or select it per case
          via the schema picker.
        </DocsCallout>
      </DocsContent>
    </DocsLayout>
  )
}

function DecisionCard({ n, title, body }: { n: string; title: string; body: string }) {
  return (
    <Card size="compact" className="flex flex-row gap-4 rounded-lg p-4 bg-card/50 my-3">
      <div className="flex-shrink-0">
        <div className="w-9 h-9 rounded-lg bg-amber-500/10 border border-amber-500/30 flex items-center justify-center text-amber-600 dark:text-amber-400 font-semibold">
          {n}
        </div>
      </div>
      <div>
        <h4 className="font-semibold text-foreground mb-1">{title}</h4>
        <p className="text-sm text-muted-foreground leading-relaxed">{body}</p>
      </div>
    </Card>
  )
}

function GroupTable({ rows }: { rows: [string, string][] }) {
  return (
    <div className="my-4 border border-border rounded-md overflow-hidden">
      <table className="w-full text-sm">
        <tbody>
          {rows.map(([k, v]) => (
            <tr key={k} className="border-b border-border last:border-0">
              <td className="block px-4 py-2 font-medium text-foreground align-top bg-card/30 md:table-cell md:w-1/3">
                {k}
              </td>
              <td className="block px-4 py-2 text-muted-foreground md:table-cell">{v}</td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  )
}
